/**
 * Edit form for a sport club.
 */
import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { Input } from '@/components/forms/Input';
import { Textarea } from '@/components/forms/Textarea';
import { Select } from '@/components/forms/Select';
import { AddressAutocompleteInput } from '@/components/forms/AddressAutocompleteInput';
import { DepartmentSelect } from '@/components/common/DepartmentSelect';
import { SPORT_TYPE_FORM_OPTIONS } from '@/components/sport-clubs/sport-club-configs';
import { sportClubsKeys } from '@/components/sport-clubs/sport-club-table-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { getSportClubById, updateSportClub } from '@/services/sport-clubs.service';
import {
  applySportClubAddressSelection,
  applySportClubFormField,
  buildUpdateSportClubDto,
  emptyCreateSportClubForm,
  isSportClubFormValid,
  sportClubToFormData,
} from '@/components/sport-clubs/sport-club-form-utils';
import type { SportClubFormValues, UpdateSportClubDto } from '@/types/sport-club.types';

/** Props for {@link EditSportClubView}. */
interface EditSportClubViewProps {
  clubId: string;
  onClose: () => void;
  onSuccess: () => void;
}

/** Edit form for a sport club. */
export const EditSportClubView: React.FC<Readonly<EditSportClubViewProps>> = ({
  clubId,
  onClose,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const {
    data: club,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: sportClubsKeys.detail(clubId),
    queryFn: () => getSportClubById(clubId),
    enabled: !!clubId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'sportClubNotFound',
    onClose,
    entityId: clubId,
  });
  const updateMutation = useMutation({
    mutationFn: (data: UpdateSportClubDto) => updateSportClub(clubId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sportClubsKeys.all });
      void queryClient.invalidateQueries({ queryKey: sportClubsKeys.detail(clubId) });
    },
  });
  const [formData, setFormData] = useState<SportClubFormValues>(emptyCreateSportClubForm);
  const [isHydrated, setIsHydrated] = useState(false);

  /** Hydrate once GET sport club resolves. */
  useEffect(() => {
    if (!club) return;
    setFormData(sportClubToFormData(club));
    setIsHydrated(true);
  }, [club]);

  /** Patch one string field. */
  const handleFieldChange = (field: keyof SportClubFormValues, value: string) => {
    setFormData((current) => applySportClubFormField(current, field, value));
  };

  /** Validate name, PATCH update DTO, then close. */
  const handleSubmit = async () => {
    if (!isSportClubFormValid(formData)) {
      toast.error(getErrorMessage('requiredClubName'));
      return;
    }

    try {
      await updateMutation.mutateAsync(buildUpdateSportClubDto(formData));
      toast.success('Club de sport modifié avec succès !');
      onSuccess();
      onClose();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <SlidePanelForm
      formType="edit"
      title="Modifier le club sportif"
      subtitle="Modifiez les informations du club sportif"
      onClose={onClose}
      onSubmit={handleSubmit}
      isSubmitting={updateMutation.isPending}
      isValid={isSportClubFormValid(formData)}
      submitLabel="Enregistrer"
      isLoading={isLoading || (!!club && !isHydrated)}
    >
      <div className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Informations de base</h3>

          <Input
            label="Nom du club"
            value={formData.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            required
            placeholder="Ex: FC Nantes"
            maxLength={255}
          />

          <Select
            label="Sport"
            placeholder="Sélectionner un sport"
            value={formData.sportType}
            onChange={(val) => handleFieldChange('sportType', val)}
            options={SPORT_TYPE_FORM_OPTIONS}
          />

          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder="Description du club..."
            rows={3}
          />
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Adresse</h3>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <AddressAutocompleteInput
              label="Adresse"
              value={formData.address}
              onChange={(value) => handleFieldChange('address', value)}
              onSelection={(result) =>
                setFormData((current) => applySportClubAddressSelection(current, result))
              }
              placeholder="Ex: Stade Municipal, 123 avenue du Sport"
              size="sm"
            />
            <Input
              label="Code postal"
              value={formData.zipCode}
              onChange={(e) => handleFieldChange('zipCode', e.target.value)}
              placeholder="Ex: 44300"
              maxLength={10}
              inputSize="sm"
            />
            <Input
              label="Ville"
              value={formData.city}
              onChange={(e) => handleFieldChange('city', e.target.value)}
              placeholder="Ex: Nantes"
              maxLength={100}
              inputSize="sm"
            />
            <Input
              label="Pays"
              value={formData.country}
              onChange={(e) => handleFieldChange('country', e.target.value)}
              placeholder="Ex: FR"
              maxLength={100}
              inputSize="sm"
            />
            <DepartmentSelect
              label="Département"
              value={formData.department}
              onChange={(val) => handleFieldChange('department', val)}
              placeholder="Sélectionner"
              inputSize="sm"
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-900">Contact</h3>

          <Input
            label="Site web"
            type="url"
            value={formData.website}
            onChange={(e) => handleFieldChange('website', e.target.value)}
            placeholder="https://fcnantes.com"
            maxLength={255}
          />

          <Input
            label="Email de contact"
            type="email"
            value={formData.contactEmail}
            onChange={(e) => handleFieldChange('contactEmail', e.target.value)}
            placeholder="contact@fcnantes.com"
            maxLength={255}
          />

          <Input
            label="Téléphone de contact"
            type="tel"
            value={formData.contactPhone}
            onChange={(e) => handleFieldChange('contactPhone', e.target.value)}
            placeholder="+33 2 40 00 00 00"
            maxLength={50}
          />
        </div>
      </div>
    </SlidePanelForm>
  );
};
